import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { fetchAcquisitions } from '../services/shopping.service';
import { addAcquisitions } from '../redux/slices/products.slice';
import { getOnboardingIA } from '../services/heru-ai.service';
import { IOnboardingResponseSamantha } from '../@types/IA.type';
import {
	setHasPendingAnalysis,
	setNss,
	setServiceStatusElement,
	setTags,
	setUserSubscriptions,
} from '../redux/slices/profile.slice';
import { getUserSuscriptions } from '../services/suscription.service';
import { linkedAccount, startLoader } from '../redux/slices/sat.slice';
import { fetchSATLinkedById } from '../services/satLinking.service';
import { ANALYTICS_EVENTS, EVENT_TYPES } from '../shared/constants/events';
import { SATLinkStatus } from '../@types/SATLinking';
import useCleverTapEvent from './useClevertapEvent';
import { getAllFft, getLastFft } from '../services/fft.service';
import { getDeclarationsOverviewDetail } from '../services/fiscal.service';
import { fetchUserRegimesIncomes } from '../services/satLinking.service';
import { REGIMES_CODE } from '../@types/Regime.type';
import {
	MonthlyDeclarationOverviewItem,
	UserRegime,
} from '../@types/TaxProfile';
import { DeclarationOverviewStatus } from '../components/organisms/DeclarationsOverview/DeclarationsOverview';
import {
	setMonthlyDeclarationsOverview,
	setUserIsPlatec,
	setIsLoadingTax,
	setAnnualDeclarationOverview,
	setUserRegimes,
	setCurrentAnnualDeclaration,
	setAllFFT,
} from '../redux/slices/tax.slice';
import { RegimeFFT } from '../@types/Products.type';
import { getSalesPeriodsAdquired } from '../services/sales.service';
import { getFiscalAnnualPreview } from '../services/tax.service';
import { AnnualPreviewEndpointResponse } from '../@types/AnnualPreview.type';
import useDeductibles from '../pages/annualDeclaration/hooks/useDeductibles';
import { useIncomes } from '../pages/annualDeclaration/hooks/useIncomes';
import { DATES } from '../shared/utils/dates';
import { getTagging } from '../services/tagging.service';
import { fetchTaxPayer } from '../services/taxes.service';
import { addTaxPayer, addUserRegimes } from '../redux/slices/taxProfile.slice';
import { getNss } from '../services/imss.service';

export const useService = () => {
	const dispatch = useDispatch();
	const logClevertapEvent = useCleverTapEvent();

	const { tokenSession } = useSelector((state: RootState) => state.auth);
	const { userHasRetentions } = useSelector(
		(state: RootState) => state.taxProfile,
	);

	const [isLoading, setIsLoading] = useState({
		adquisitions: false,
		onboardingIAProgress: false,
		subscriptions: false,
		serviceStatus: false,
		satLinkStatus: true,
		declarationsOverviewDetail: false,
		userIsPlatec: false,
		annualDeclarationOverview: false,
		allFFT: false,
	});

	const getAcquisitions = async () => {
		try {
			setIsLoading({ ...isLoading, adquisitions: true });
			const response = await fetchAcquisitions(tokenSession);
			dispatch(addAcquisitions(response));
		} catch (error) {
		} finally {
			setIsLoading({ ...isLoading, adquisitions: false });
		}
	};

	const getSubscriptions = async () => {
		try {
			setIsLoading({ ...isLoading, subscriptions: true });
			const subs = await getUserSuscriptions(tokenSession);
			dispatch(setUserSubscriptions(subs));
		} catch (error) {
		} finally {
			setIsLoading({ ...isLoading, subscriptions: false });
		}
	};

	const getOnboardingIAProgress = async (userId: number) => {
		try {
			setIsLoading({ ...isLoading, onboardingIAProgress: true });
			const response: IOnboardingResponseSamantha = await getOnboardingIA(
				userId,
				tokenSession,
			);

			if (response?.percentage?.percentage) {
				if (response.percentage.percentage !== 100) {
					dispatch(setHasPendingAnalysis(true));
				}
			}
		} catch (error) {
		} finally {
			setIsLoading({ ...isLoading, onboardingIAProgress: false });
		}
	};

	const getSatLinkedAccount = async (userId: number, userPhone: string) => {
		try {
			dispatch(startLoader(true));
			setIsLoading({ ...isLoading, satLinkStatus: true });
			const resource = await fetchSATLinkedById(tokenSession);
			dispatch(linkedAccount(resource));
			logClevertapEvent(ANALYTICS_EVENTS.HOME_LINK, {
				event_type: EVENT_TYPES.HOME_MAIN_SCREEN_VIEWED,
				id: userId,
				phone: userPhone,
				link: resource.status === SATLinkStatus.ACTIVE,
			});
		} catch (error: any) {
			dispatch(startLoader(false));
		} finally {
			setIsLoading({ ...isLoading, satLinkStatus: false });
		}
	};

	const fetchSalesPeriodsAdquired = async () => {
		try {
			const responsePLATEC = await getSalesPeriodsAdquired({
				tokenSession,
				regime: RegimeFFT[REGIMES_CODE.PLATAFORMAS_TECNOLOGICAS],
				period_type: 'monthly',
			});
			const responseRESICO = await getSalesPeriodsAdquired({
				tokenSession,
				regime: RegimeFFT[REGIMES_CODE.RESICO],
				period_type: 'monthly',
			});
			const mappedPLATEC = responsePLATEC?.monthly?.map((period: any) => ({
				...period,
				regime: 'platec',
			}));
			const mappedRESICO = responseRESICO?.monthly?.map((period: any) => ({
				...period,
				regime: 'resico',
			}));

			return [...mappedPLATEC, ...mappedRESICO];
		} catch (error) {}
	};

	const fetchLastDeclarationStatus = async (id: number) => {
		try {
			setIsLoading({ ...isLoading, serviceStatus: true });
			const response = await getLastFft(id, tokenSession);
			if (response) {
				if (response.resource?.statusCode === 500) {
					return;
				} else if (Object.keys(response.resource).length > 0) {
					dispatch(setServiceStatusElement(response.resource));
				}
			}
		} catch (error) {
		} finally {
			setIsLoading({ ...isLoading, serviceStatus: false });
		}
	};

	const fetchUserRegimes = async () => {
		try {
			dispatch(setIsLoadingTax({ key: 'regimes', value: true }));
			const userRegimes = await fetchUserRegimesIncomes(tokenSession);
			dispatch(setUserRegimes(userRegimes));
			return userRegimes;
		} catch (error) {
		} finally {
			dispatch(setIsLoadingTax({ key: 'regimes', value: false }));
		}
	};

	const fetchUserIsPlatec = async () => {
		try {
			dispatch(setIsLoadingTax({ key: 'userIsPlatec', value: true }));
			const userRegimes = await fetchUserRegimesIncomes(tokenSession);
			const userHasPlatecRegime =
				userRegimes &&
				userRegimes?.some(
					(regime: UserRegime) =>
						regime?.code === REGIMES_CODE.PLATAFORMAS_TECNOLOGICAS,
				);

			if (!userHasPlatecRegime) {
				const userIsPlatec = !!userHasRetentions?.hasRetentions;
				dispatch(setUserIsPlatec(userIsPlatec));
				return userIsPlatec;
			}

			dispatch(setUserIsPlatec(userHasPlatecRegime));
			return userHasPlatecRegime;
		} catch (error) {
			return false;
		} finally {
			dispatch(setIsLoadingTax({ key: 'userIsPlatec', value: false }));
		}
	};

	const fetchAnnualDeclarationOverview = async () => {
		try {
			setIsLoading({ ...isLoading, annualDeclarationOverview: true });
			const response = (await getFiscalAnnualPreview(
				'2024',
			)) as AnnualPreviewEndpointResponse;

			const { formattedDeductions, totalDeductions } = useDeductibles(response);
			const { formattedIncomes } = useIncomes(response);

			const annualDeclaration = {
				general: [
					{
						label: 'Ingresos identificados',
						value: formattedIncomes.total_incomes,
					},
					{
						label: 'Deducciones personales',
						value: totalDeductions,
					},
				],
				deductibles: formattedDeductions,
				incomes: formattedIncomes,
				result: {
					total: totalDeductions,
					type: 'A favor',
					total_incomes: formattedIncomes.total_incomes,
					total_deductions: totalDeductions,
					total_isr_retained: formattedIncomes.total_isr_retained,
					estimated_result: formattedIncomes.total_incomes - totalDeductions,
				},
			};
			dispatch(setAnnualDeclarationOverview(annualDeclaration));
		} catch (error) {
		} finally {
			setIsLoading({ ...isLoading, annualDeclarationOverview: false });
		}
	};

	const fetchDeclarationsOverviewDetail = async () => {
		try {
			if (isLoading.declarationsOverviewDetail) return;
			dispatch(setMonthlyDeclarationsOverview(null));
			dispatch(
				setIsLoadingTax({ key: 'declarationsOverviewDetail', value: true }),
			);
			const salesPeriodsAdquired = await fetchSalesPeriodsAdquired();
			const response =
				(await getDeclarationsOverviewDetail()) as MonthlyDeclarationOverviewItem[];

			const responseWithoutAnnual = response
				?.filter((declaration) => declaration?.type !== 'annual')
				?.sort((a, b) => {
					const [yearA, yearB] = [a.presented_at, b.presented_at].map(
						(year) => year,
					);
					if (!yearA || !yearB) return 0;
					return new Date(yearB).getTime() - new Date(yearA).getTime();
				});

			const annualsWithoutDuplicates = (() => {
				const annualsByYear = new Map<string, any>();
				response
					?.filter((declaration) => declaration?.type === 'annual')
					?.forEach((declaration) => {
						const year = declaration?.declaration_year?.split('-')[0];

						if (!year) return;

						if (
							!annualsByYear.has(year) ||
							(declaration?.presented_at &&
								(!annualsByYear.get(year)?.presented_at ||
									new Date(declaration.presented_at).getTime() >
										new Date(annualsByYear.get(year).presented_at).getTime()))
						) {
							annualsByYear.set(year, declaration);
						}
					});

				return Array.from(annualsByYear.values());
			})()?.map((declaration) => {
				return {
					...declaration,
					declaration_year: Number(
						declaration?.declaration_year?.split('-')[0],
					),
				};
			});

			const currentAnnualDeclaration = annualsWithoutDuplicates.find(
				(declaration) =>
					declaration?.declaration_year === DATES.currentYear - 1,
			);

			dispatch(setCurrentAnnualDeclaration(currentAnnualDeclaration));

			const annualDeclarations = annualsWithoutDuplicates
				// ONLY ANNUAL
				?.filter((declaration) => declaration?.type === 'annual')

				// VALIDATIONS
				?.filter((declaration) => {
					const year = declaration?.declaration_year;
					const isFutureDeclaration = Number(year) + 2 > DATES.currentYear;
					const isBeforeFiveYears = Number(year) < DATES.currentYear - 6;
					const isPending = [
						DeclarationOverviewStatus?.MISSING,
						DeclarationOverviewStatus?.MISSING_WITHOUT_INCOME,
					].includes(declaration?.obligation_compliance_check);

					if (!declaration?.obligation_compliance_check) return false;
					if (isFutureDeclaration) return false;
					if (isBeforeFiveYears && isPending) return false;

					return true;
				})
				?.sort((a, b) => {
					const [yearA, yearB] = [a.declaration_year, b.declaration_year].map(
						(year) => year,
					);
					if (!yearA || !yearB) return 0;
					return yearB - yearA;
				});

			// MONTHLY
			const removeLastDeclaration = responseWithoutAnnual?.filter(
				(declaration) => {
					if (
						declaration?.obligation_compliance_check &&
						declaration?.obligation_compliance_check.toString() ===
							DeclarationOverviewStatus.PRESENTED.toString()
					)
						return true;

					const periodParts =
						declaration.declaration_period?.split('-').map(Number) || [];
					const month = periodParts?.[0];
					const year = periodParts?.[1];
					const isLastDeclaration =
						Number(year) === DATES.currentYear &&
						Number(month) >= DATES.currentMonth - 1;

					return !isLastDeclaration;
				},
			);

			let filteredDeclarationsWithoutIncome: MonthlyDeclarationOverviewItem[] =
				[];

			if (removeLastDeclaration && removeLastDeclaration.length > 0) {
				const orderedResponse = removeLastDeclaration.sort((a: any, b: any) => {
					const [monthA, yearA] = a.declaration_period?.split('-');
					const [monthB, yearB] = b.declaration_period?.split('-');

					const yearComparison = parseInt(yearB) - parseInt(yearA);
					if (yearComparison !== 0) return yearComparison;

					return parseInt(monthB) - parseInt(monthA);
				});

				const latestDeclarationsByPeriod = new Map<string, any>();

				orderedResponse.forEach((declaration: any) => {
					const period = declaration.declaration_period;
					const type = declaration.type;
					const compositeKey = `${period}-${type}`;

					if (
						!latestDeclarationsByPeriod.has(compositeKey) ||
						(declaration?.presented_at &&
							new Date(declaration?.presented_at).getTime() >
								new Date(
									latestDeclarationsByPeriod.get(compositeKey)?.presented_at,
								).getTime())
					) {
						latestDeclarationsByPeriod.set(compositeKey, declaration);
					}
				});
				const uniqueLatestDeclarations = Array.from(
					latestDeclarationsByPeriod.values(),
				);
				const filteredDeclarations = uniqueLatestDeclarations.filter(
					(declaration) =>
						declaration.obligation_compliance_check &&
						[
							DeclarationOverviewStatus.PRESENTED,
							DeclarationOverviewStatus.MISSING,
							DeclarationOverviewStatus.MISSING_WITHOUT_INCOME,
						].includes(declaration.obligation_compliance_check),
				);

				filteredDeclarationsWithoutIncome = filteredDeclarations.map(
					(declaration: MonthlyDeclarationOverviewItem) => {
						return {
							...declaration,
							obligation_compliance_check:
								declaration?.obligation_compliance_check ===
								DeclarationOverviewStatus.MISSING_WITHOUT_INCOME
									? DeclarationOverviewStatus?.MISSING
									: declaration.obligation_compliance_check,
						};
					},
				);

				if (salesPeriodsAdquired && salesPeriodsAdquired.length > 0) {
					filteredDeclarationsWithoutIncome.forEach(
						(declaration: MonthlyDeclarationOverviewItem) => {
							const periodParts =
								declaration.declaration_period?.split('-').map(Number) || [];
							const month = periodParts?.[0];
							const year = periodParts?.[1];

							const isPeriodAcquired = salesPeriodsAdquired.some(
								(period: { year: number; month: number; regime: string }) => {
									return (
										Number(period.year) === Number(year) &&
										Number(period.month) === Number(month) &&
										period.regime === declaration.type
									);
								},
							);

							//@ts-ignore
							if (isPeriodAcquired) {
								declaration.period_hired_with_heru = 'Periodo contratado';
							}
						},
					);
				}
			}

			const allDeclarations = [
				...filteredDeclarationsWithoutIncome,
				...annualDeclarations,
			];

			dispatch(setMonthlyDeclarationsOverview(allDeclarations));
		} catch (error) {
		} finally {
			dispatch(
				setIsLoadingTax({ key: 'declarationsOverviewDetail', value: false }),
			);
		}
	};

	const getFFTs = async (userId: number, year: string) => {
		try {
			setIsLoading({ ...isLoading, allFFT: true });
			const response = await getAllFft(userId, tokenSession, year);
			dispatch(setAllFFT(response));
		} catch (error) {
		} finally {
			setIsLoading({ ...isLoading, allFFT: false });
		}
	};

	const getTaggingUsers = async () => {
		try {
			const response = await getTagging(tokenSession);
			dispatch(setTags(response));
		} catch (error) {}
	};

	const getTaxPayer = async (userId: number) => {
		try {
			if (!userId) return;
			dispatch(startLoader(true));
			const resource = await fetchTaxPayer(userId, tokenSession);
			dispatch(addTaxPayer(resource));
		} catch (error: any) {
		} finally {
			dispatch(startLoader(false));
		}
	};

	const getNssInfo = async () => {
		try {
			const resource = await getNss(tokenSession);
			dispatch(setNss(resource));
		} catch (error: any) {
		} finally {
		}
	};

	const getUserRegimes = async () => {
		try {
			const response = await fetchUserRegimesIncomes(tokenSession);
			dispatch(addUserRegimes(response));
		} catch (error) {}
	};

	return {
		isLoading,
		getAcquisitions,
		getOnboardingIAProgress,
		getSubscriptions,
		getSatLinkedAccount,
		fetchLastDeclarationStatus,
		fetchDeclarationsOverviewDetail,
		fetchUserIsPlatec,
		fetchSalesPeriodsAdquired,
		fetchAnnualDeclarationOverview,
		fetchUserRegimes,
		getFFTs,
		getTaggingUsers,
		getTaxPayer,
		getNssInfo,
		getUserRegimes,
	};
};
